import {
  DomainColors,
  FrequencyTypeLabels,
  FrequencyTypePluralLabels,
} from "@/domain/contracts";
import {
  Patient,
  PatientDetailAppointment,
  PatientRecurrency,
  PatientRecurrentAppointment,
  PatientSingleAppointment,
} from "@/domain/entities";
import { PatientMapper } from "@/domain/mappers";
import {
  fixFrequency,
  getAppointmentStatusColor,
  getAppointmentStatusLabel,
  getFrequencyDateText,
} from "@/data/utils";
import {
  PatientDetailsResponse,
  PatientDetailsResponseAppointments,
  PatientsResponseAppointment,
} from "../dtos/responses";

export class PatientMapperImpl implements PatientMapper<
  PatientDetailsResponse,
  PatientsResponseAppointment,
  PatientDetailsResponseAppointments
> {
  toDomain(data: PatientDetailsResponse): Patient {
    return {
      user: {
        id: data.id,
        name: data.name,
        profile_image: data.profile_image,
        handicapped: data.handicapped,
        deleted_at: data.deleted_at,
      },
      chat: data.chat ? { ...data.chat, user: { name: data.name } } : null,
      appointments: data.appointments.map((app) =>
        this.toPatientDetailAppointment(app),
      ),
      recurrencies: data.recurrencies.map((rec) =>
        this.toPatientRecurrency(rec),
      ),
    };
  }

  toPatientSingleAppointment(
    data: PatientsResponseAppointment,
  ): PatientSingleAppointment {
    return {
      id: data.id,
      name: data.name,
      profile_image: data.profile_image,
      valid_appointments_count_formatted: `${data.valid_appointments_count} ${data.valid_appointments_count === 1 ? "Sessão" : "Sessões"}`,
    };
  }

  toPatientRecurrentAppointment(
    data: PatientsResponseAppointment,
  ): PatientRecurrentAppointment {
    let latest_recurrency_frequency = "";

    if (data.latest_recurrency?.frequency) {
      const frequency = fixFrequency(
        data.latest_recurrency.frequency,
        data.latest_recurrency.interval,
      );

      latest_recurrency_frequency = FrequencyTypeLabels[frequency];
    }

    return {
      id: data.id,
      name: data.name,
      profile_image: data.profile_image,
      latest_recurrency_frequency,
    };
  }

  toPatientRecurrency(
    data: PatientDetailsResponseAppointments,
  ): PatientRecurrency {
    let title = "";
    let description = "";

    if (data.frequency) {
      const frequency = fixFrequency(data.frequency, data.interval);

      title = `Sessões ${FrequencyTypePluralLabels[frequency]}`;

      description = getFrequencyDateText(
        frequency,
        data.interval,
        data.reference_date,
        true,
      );
    }

    return {
      id: data.id,
      title,
      description,
      value: data.value,
      status_label: data.status === "active" ? "Ativa" : "Cancelada",
      status_bg_color:
        data.status === "active"
          ? DomainColors.success
          : DomainColors.primary50,
    };
  }

  toPatientDetailAppointment(
    data: PatientDetailsResponseAppointments,
  ): PatientDetailAppointment {
    return {
      id: data.id,
      date: data.date,
      value: data.value,
      status: data.status,
      status_label: getAppointmentStatusLabel({ status: data.status }),
      status_color: getAppointmentStatusColor({ status: data.status }),
      pg_status: data.pg_status,
      charge_status: data.charge_status,
      refund_status: data.refund_status,
    };
  }
}
